import React, { useEffect, useState, useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { api } from '@/api/client';
import { PAYMENT_STATUSES, formatNumber, today } from '@/utils/helpers';
import { useCurrency, formatCurrency } from '@/hooks/useCurrency';
import { Plus, Trash, PackagePlus } from 'lucide-react';
import { ProductPicker } from '@/components/ui/ProductPicker';
import { SupplierSelect } from '@/components/forms/SupplierSelect';

const emptyItem = () => ({
  product_id: '',
  product_name: '',
  qty: 1,
  cost: 0,
  selling_price: 0,
  warranty_months: 0,
  condition: 'New',
  serials: [],
  _serial_text: '' // textarea content
});

export const GRNForm = ({ isOpen, onClose, grn = null, onSaved }) => {
  const currency = useCurrency();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newProductRowIndex, setNewProductRowIndex] = useState(null); // which GRN row triggered Create New

  const [formData, setFormData] = useState({
    supplier_id: '',
    supplier_name: '',
    invoice_number: '',
    received_date: today(),
    items: [emptyItem()],
    payment_status: 'Credit',
    due_date: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadProducts();
      if (grn) {
        setFormData({
          supplier_id: grn.supplier_id || '',
          supplier_name: grn.supplier_name || '',
          invoice_number: grn.invoice_number || '',
          received_date: grn.received_date || today(),
          items: (grn.items || []).map(item => ({
            ...item,
            _serial_text: (item.serials || []).join('\n')
          })) || [emptyItem()],
          payment_status: grn.payment_status || 'Credit',
          due_date: grn.due_date || ''
        });
      } else {
        setFormData({
          supplier_id: '',
          supplier_name: '',
          invoice_number: '',
          received_date: today(),
          items: [emptyItem()],
          payment_status: 'Credit',
          due_date: ''
        });
      }
    }
  }, [isOpen, grn]);

  const loadProducts = async () => {
    try {
      const res = await api.getProducts();
      setProducts(res.data);
    } catch (e) { console.error(e); }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };

    if (field === 'product_id') {
      const product = products.find(p => p.id === value);
      if (product) {
        newItems[index].product_name = product.name;
        newItems[index].cost = product.cost;
      }
    }

    if (field === '_serial_text') {
      // Parse serials from textarea (newline or comma separated)
      const serials = value
        .split(/[\n,]+/)
        .map(s => s.trim())
        .filter(s => s);
      newItems[index].serials = serials;
      newItems[index].qty = serials.length || newItems[index].qty;
    }

    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => setFormData({ ...formData, items: [...formData.items, emptyItem()] });
  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  };

  // Called by NewProductModal after a successful create — assign new product to the originating row.
  const handleProductCreated = (rowIdx, product) => {
    setProducts((prev) => [...prev, product]);
    const newItems = [...formData.items];
    if (newItems[rowIdx]) {
      newItems[rowIdx] = {
        ...newItems[rowIdx],
        product_id: product.id,
        product_name: product.name,
        warranty_months: product.warranty || newItems[rowIdx].warranty_months || 0,
        condition: product.condition_type || newItems[rowIdx].condition || 'New',
        selling_price: product.price || newItems[rowIdx].selling_price || 0
      };
      setFormData({ ...formData, items: newItems });
    }
    setNewProductRowIndex(null);
  };

  const totalValue = useMemo(() => {
    return formData.items.reduce((sum, item) => sum + (item.cost * item.qty), 0);
  }, [formData.items]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.supplier_name) {
      alert('Please select or add a supplier');
      return;
    }
    if (formData.items.some(i => !i.product_id)) {
      alert('Please select a product for each line item');
      return;
    }
    if (formData.payment_status === 'Credit' && !formData.due_date) {
      alert('Due date is required for Credit payment status');
      return;
    }

    // Strip _serial_text helper before sending
    const cleanItems = formData.items.map(({ _serial_text, ...rest }) => rest);
    const payload = { ...formData, items: cleanItems };

    setLoading(true);
    try {
      if (grn) {
        await api.updateGRN(grn.id, payload);
      } else {
        await api.createGRN(payload);
      }
      onSaved && onSaved();
      onClose();
    } catch (error) {
      console.error(error);
      alert('Failed to save GRN');
    } finally { setLoading(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={grn ? `Edit ${grn.grn_number || 'GRN'}` : 'Create New GRN'} size="xl">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Internal GRN Number badge */}
        {grn?.grn_number && (
          <div className="flex items-center gap-2 text-xs" data-testid="grn-internal-number">
            <span className="text-[var(--text-muted)]">Internal GRN #</span>
            <span className="font-mono font-bold text-blue-600 dark:text-blue-400">{grn.grn_number}</span>
          </div>
        )}
        {!grn && (
          <p className="text-xs text-[var(--text-muted)] bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-2">
            An internal GRN number (GRN-000xxx) will be assigned automatically when you save.
          </p>
        )}

        {/* Supplier Info */}
        <div className="grid grid-cols-3 gap-4">
          <SupplierSelect
            value={{ supplier_id: formData.supplier_id, supplier_name: formData.supplier_name }}
            onChange={({ supplier_id, supplier_name }) => setFormData({ ...formData, supplier_id, supplier_name })}
            required
            testId="grn-supplier"
          />
          <div>
            <Input label="Supplier Invoice #" placeholder="Leave blank if none"
              value={formData.invoice_number}
              onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
              data-testid="grn-invoice-number" />
            <p className="text-[10px] text-[var(--text-muted)] mt-1">
              Optional — leave blank if the supplier didn&apos;t provide one.
            </p>
          </div>
          <Input label="Received Date *" type="date" value={formData.received_date} onChange={(e) => setFormData({ ...formData, received_date: e.target.value })} data-testid="grn-received-date" />
        </div>

        {/* Items */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Items Received</h3>
            <Button type="button" size="sm" onClick={addItem} data-testid="grn-add-item">
              <Plus size={14} className="mr-1" />
              Add Item
            </Button>
          </div>

          <div className="space-y-3">
            {formData.items.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 items-start p-3 bg-gray-50 dark:bg-gray-900 rounded-lg" data-testid={`grn-item-${index}`}>
                <div className="col-span-4">
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Product *</label>
                  {item.product_id ? (
                    <div className="flex items-center justify-between px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md">
                      <span className="font-medium truncate">{item.product_name}</span>
                      <button type="button" onClick={() => updateItem(index, 'product_id', '')} className="text-xs text-blue-600 hover:underline ml-2" data-testid={`grn-change-product-${index}`}>Change</button>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <ProductPicker
                        onSelect={(p) => {
                          const newItems = [...formData.items];
                          newItems[index] = {
                            ...newItems[index],
                            product_id: p.id,
                            product_name: p.name,
                            warranty_months: p.warranty || newItems[index].warranty_months || 0,
                            condition: p.condition_type || newItems[index].condition || 'New',
                            selling_price: p.price || newItems[index].selling_price || 0,
                            cost: newItems[index].cost || 0
                          };
                          setFormData({ ...formData, items: newItems });
                        }}
                        placeholder="Search inventory..."
                        testId={`grn-item-picker-${index}`}
                      />
                      <button
                        type="button"
                        onClick={() => setNewProductRowIndex(index)}
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                        data-testid={`grn-new-product-${index}`}
                      >
                        <PackagePlus size={12} /> Create new product
                      </button>
                    </div>
                  )}
                </div>

                <div className="col-span-5">
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block flex items-center gap-2">
                    Serial Numbers (one per line, comma-separated, or scan barcode)
                    <span className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded text-[10px] font-medium">📷 Scanner Ready</span>
                  </label>
                  <Textarea
                    value={item._serial_text || ''}
                    onChange={(e) => updateItem(index, '_serial_text', e.target.value)}
                    onKeyDown={(e) => {
                      // Barcode scanners send Enter at the end. Convert Enter to newline
                      // and prevent form submission
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const target = e.target;
                        const value = target.value;
                        const cursor = target.selectionStart;
                        const newValue = value.slice(0, cursor) + '\n' + value.slice(cursor);
                        updateItem(index, '_serial_text', newValue);
                        // Move cursor after the newline
                        setTimeout(() => {
                          target.setSelectionRange(cursor + 1, cursor + 1);
                        }, 0);
                      }
                    }}
                    rows={3}
                    placeholder="SN001&#10;SN002&#10;SN003&#10;(scan barcode here)"
                    data-testid={`grn-item-serials-${index}`}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {item.serials?.length || 0} serial(s) detected
                  </p>
                </div>

                <div className="col-span-1">
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Qty</label>
                  <Input type="number" min="1" value={item.qty} onChange={(e) => updateItem(index, 'qty', parseInt(e.target.value) || 1)} data-testid={`grn-item-qty-${index}`} />
                </div>

                <div className="col-span-1">
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Unit Cost</label>
                  <Input type="number" step="0.01" min="0" value={item.cost} onChange={(e) => updateItem(index, 'cost', parseFloat(e.target.value) || 0)} data-testid={`grn-item-cost-${index}`} />
                </div>

                <div className="col-span-1 flex items-end justify-center pt-5">
                  <Button type="button" size="sm" variant="ghost" onClick={() => removeItem(index)} disabled={formData.items.length === 1}>
                    <Trash size={16} className="text-red-600" />
                  </Button>
                </div>

                {/* Row 2: Batch metadata (selling price / warranty / condition) */}
                <div className="col-span-12 grid grid-cols-12 gap-2 mt-2 pl-1">
                  <div className="col-span-3">
                    <label className="text-xs text-gray-500 mb-1 block">Selling Price (optional)</label>
                    <Input type="number" step="0.01" min="0" value={item.selling_price || 0}
                      onChange={(e) => updateItem(index, 'selling_price', parseFloat(e.target.value) || 0)}
                      placeholder="Override product price"
                      data-testid={`grn-item-sell-${index}`} />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 mb-1 block">Warranty (mo)</label>
                    <Input type="number" min="0" value={item.warranty_months || 0}
                      onChange={(e) => updateItem(index, 'warranty_months', parseInt(e.target.value) || 0)}
                      data-testid={`grn-item-warranty-${index}`} />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 mb-1 block">Condition</label>
                    <Select value={item.condition || 'New'}
                      onChange={(e) => updateItem(index, 'condition', e.target.value)}
                      data-testid={`grn-item-condition-${index}`}>
                      <option value="New">New</option>
                      <option value="Used">Used</option>
                      <option value="Refurbished">Refurbished</option>
                    </Select>
                  </div>
                  <div className="col-span-5 text-xs text-gray-500 self-end pb-2">
                    These values are stored on each received serial for full traceability.
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Info */}
        <div className="grid grid-cols-3 gap-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
          <Select label="Payment Status" value={formData.payment_status} onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })} data-testid="grn-payment-status">
            {PAYMENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Input
            label="Due Date"
            type="date"
            value={formData.due_date}
            onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
            disabled={formData.payment_status === 'Paid'}
            data-testid="grn-due-date"
          />
          <div className="flex items-end">
            <div className="px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg w-full">
              <div className="text-xs text-gray-600 dark:text-gray-400">Total Value</div>
              <div className="text-lg font-bold text-blue-700 dark:text-blue-300">{formatCurrency(totalValue, currency)}</div>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg text-xs text-yellow-800 dark:text-yellow-200">
          ℹ️ On save, all listed serials will be automatically added to the product inventory with status "In Stock".
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={loading} data-testid="save-grn">
            {loading ? 'Saving...' : (grn ? 'Update GRN' : 'Create GRN')}
          </Button>
        </div>
      </form>

      {newProductRowIndex !== null && (
        <NewProductModal
          onClose={() => setNewProductRowIndex(null)}
          onCreated={(p) => handleProductCreated(newProductRowIndex, p)}
        />
      )}
    </Modal>
  );
};

// ===================== Inline "Create New Product" modal =====================
const CATEGORIES = ['RAM', 'SSD', 'HDD', 'GPU', 'CPU', 'Motherboard', 'PSU', 'Case', 'Cooler', 'Laptop', 'Desktop', 'Monitor', 'Keyboard', 'Mouse', 'Headset', 'Cable', 'Adapter', 'Thermal Paste', 'Accessories', 'Other'];

const NewProductModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: '',
    category: 'RAM',
    brand: '',
    model: '',
    sku: '',
    barcode: '',
    serial_required: true,
    condition_type: 'New',
    warranty: 12,
    price: 0,
    low_stock: 5,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { alert('Product name is required'); return; }
    if (!form.category.trim()) { alert('Category is required'); return; }
    setSaving(true);
    try {
      const res = await api.createProduct({
        ...form,
        warranty: parseInt(form.warranty) || 0,
        price: parseFloat(form.price) || 0,
        low_stock: parseInt(form.low_stock) || 5,
      });
      onCreated(res.data);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to create product');
    } finally { setSaving(false); }
  };

  return (
    <Modal isOpen onClose={onClose} title="Create New Product" size="lg">
      <form onSubmit={submit} className="space-y-3" data-testid="new-product-modal">
        <p className="text-xs text-[var(--text-muted)]">
          This product will be added to the master list and assigned to your GRN line in one step.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Product Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Corsair Vengeance 16GB DDR4 3200" autoFocus data-testid="new-prod-name" />
          <Select label="Category *" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="new-prod-category">
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Corsair, Samsung, ASUS..." data-testid="new-prod-brand" />
          <Input label="Model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Vengeance LPX, 870 EVO..." data-testid="new-prod-model" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="SKU / Internal Code" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="new-prod-sku" />
          <Input label="Barcode (optional)" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} placeholder="Scan or type" data-testid="new-prod-barcode" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Serial Required?</label>
            <Select value={form.serial_required ? 'yes' : 'no'} onChange={(e) => setForm({ ...form, serial_required: e.target.value === 'yes' })} data-testid="new-prod-serial-required">
              <option value="yes">Yes — each unit has unique SN</option>
              <option value="no">No — qty only (cables, paste, etc.)</option>
            </Select>
          </div>
          <Select label="Default Condition" value={form.condition_type} onChange={(e) => setForm({ ...form, condition_type: e.target.value })} data-testid="new-prod-condition">
            <option value="New">New</option>
            <option value="Used">Used</option>
            <option value="Refurbished">Refurbished</option>
          </Select>
          <Input label="Default Warranty (months)" type="number" min={0} value={form.warranty} onChange={(e) => setForm({ ...form, warranty: e.target.value })} data-testid="new-prod-warranty" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Suggested Selling Price" type="number" step="0.01" min={0} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="new-prod-price" />
          <Input label="Low-Stock Threshold" type="number" min={0} value={form.low_stock} onChange={(e) => setForm({ ...form, low_stock: e.target.value })} data-testid="new-prod-lowstock" />
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-[var(--border)]">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="save-new-product">
            <PackagePlus size={14} className="mr-1" /> {saving ? 'Creating...' : 'Create & Assign'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
